/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type ImageLayer from 'ol/layer/Image';
import TileLayer from 'ol/layer/WebGLTile';
import { GeoTIFF } from 'ol/source';
import { useContext, useEffect, useRef, useState } from 'react';
import type { LayerFoundation } from '@opengeoweb/webmap';
import { useFetchGeoTIFFBlob } from '../../../geotiff-layer-api/hooks';
import MapContext from '../context/MapContext';

export interface TiledWMSLayerProps {
  url: string;
  layerName: string;
  layerOptions: ConstructorParameters<typeof ImageLayer>[0];
  layerProps?: LayerFoundation;
  projection?: string;
  shouldRender?: boolean;
}

export const GeoTIFFLayer: React.FC<TiledWMSLayerProps> = ({
  url,
  layerName,
  layerOptions,
  layerProps,
  projection,
  shouldRender = true,
}: TiledWMSLayerProps) => {
  const { map } = useContext(MapContext);
  const layerRef = useRef<TileLayer>();
  const [initialized, setInitialized] = useState<boolean>(false);
  const { data: blob, isSuccess } = useFetchGeoTIFFBlob(url);

  // Only create the layer once
  useEffect(() => {
    if (
      !map ||
      !layerName ||
      layerRef.current ||
      !isSuccess ||
      !blob ||
      !shouldRender
    ) {
      return (): void => {};
    }

    const source = new GeoTIFF({
      sources: [
        {
          blob,
        },
      ],
      transition: 0,
      projection,
    });

    const layer = new TileLayer({
      source,
      zIndex: 0,
    });

    map.addLayer(layer);
    layerRef.current = layer;

    setInitialized(true);

    return (): void => {
      map.removeLayer(layerRef.current!);
      layerRef.current = undefined;
      setInitialized(false);
    };
  }, [map, layerName, blob, isSuccess, projection, shouldRender]);

  // Update layer options
  useEffect(() => {
    const layer = layerRef.current;
    if (!layerOptions || !layer || !initialized) {
      return;
    }
    if (layerOptions.opacity !== undefined && layerOptions.opacity !== null) {
      layer.setOpacity(layerOptions.opacity);
    }
    if (layerOptions.zIndex !== undefined && layerOptions.zIndex !== null) {
      layer.setZIndex(layerOptions.zIndex);
    }
  }, [layerOptions, initialized]);

  // Update visibility
  useEffect(() => {
    const layer = layerRef.current;
    if (!layerProps || !layer || !initialized) {
      return;
    }

    layer.setVisible(!!layerProps.enabled);
  }, [layerProps, initialized]);

  return null;
};
